// ==========================================================================
// CONTEXTUAL NAV — Generic global ↔ contextual sticky row crossfade
//
// Any page that renders a .contextual-nav-anchor element in the flow will
// trigger the swap. Adding a new page = create the snippet + the anchor.
// No changes needed here.
// ==========================================================================

export function initContextualNav() {
  const nav = document.getElementById('nav');
  if (!nav) return;

  const anchor = document.querySelector('.contextual-nav-anchor');
  if (!anchor) return;

  nav.classList.add('nav--has-contextual-swap');

  const page = document.body.dataset.page;
  const ctxHoverPages = ['projects', 'studio'];

  let scrollPeek   = false;
  let mousePeek    = false;
  let ctxHoverPeek = false;

  function updatePeek() {
    // ctxHoverPeek guards peek: while hovering an excluded zone, never show global row
    const next = (scrollPeek || mousePeek) && !ctxHoverPeek;
    nav.classList.toggle('nav--peek', next);
  }

  function setCtxHover(val) {
    if (ctxHoverPeek === val) return;
    ctxHoverPeek = val;
    nav.classList.toggle('nav--ctx-hover', val);
  }

  const navH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav-global-h')) || 75;

  new IntersectionObserver(([entry]) => {
    // Anchor below viewport — haven't scrolled past it yet, don't activate.
    // Use >= innerHeight (not > 0) so a partially-scrolled-past anchor (small
    // positive top) doesn't falsely trigger the guard and kill the swap.
    if (!entry.isIntersecting && entry.boundingClientRect.top >= window.innerHeight) {
      nav.classList.remove('nav--contextual-active');
      return;
    }
    const active = !entry.isIntersecting;
    nav.classList.toggle('nav--contextual-active', active);
    if (entry.isIntersecting) {
      scrollPeek = false;
      mousePeek  = false;
      updatePeek();
    }
  }, { threshold: 0, rootMargin: `-${navH}px 0px 0px 0px` }).observe(anchor);

  // Pre-fade: triggers ~60px before the main swap so anchor elements start
  // fading out before nav--contextual-active fires.
  new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting && entry.boundingClientRect.top >= window.innerHeight) {
      nav.classList.remove('nav--approaching');
      return;
    }
    nav.classList.toggle('nav--approaching', !entry.isIntersecting);
  }, { threshold: 0, rootMargin: `-${navH + 80}px 0px 0px 0px` }).observe(anchor);

  if (window.matchMedia('(pointer: coarse)').matches) return;

  // ── Scroll-up peek (desktop only) ─────────────────────────────────────────
  let lastScrollY = window.scrollY;
  let scrollRafId = null;

  window.addEventListener('scroll', () => {
    if (scrollRafId) return;
    scrollRafId = requestAnimationFrame(() => {
      scrollRafId = null;
      const curr = window.scrollY;
      if (nav.classList.contains('nav--contextual-active')) {
        const prev = scrollPeek;
        scrollPeek = curr < lastScrollY && curr > 0;
        updatePeek();
      }
      lastScrollY = curr;
    });
  }, { passive: true });

  // ── Hover peek (desktop only) ─────────────────────────────────────────────
  // Hover anywhere on the nav except the excluded interaction zones shows the
  // global row. Excluded: .nav__contextual-left (category buttons / project info)
  // and .projects__sticky-nav__right (Grid/List toggle).

  let mouseRafId     = null;
  let mouseleaveTimer = null;

  nav.addEventListener('mousemove', (e) => {
    if (mouseleaveTimer) { clearTimeout(mouseleaveTimer); mouseleaveTimer = null; }
    if (mouseRafId) return;
    mouseRafId = requestAnimationFrame(() => {
      mouseRafId = null;
      const isContextual = nav.classList.contains('nav--contextual-active');
      if (!isContextual) {
        if (mousePeek) { mousePeek = false; updatePeek(); }
        setCtxHover(false);
        return;
      }

      // Excluded zones: hovering them shows background only (nav--ctx-hover), never the global row.
      const overExcluded = !!e.target.closest('.nav__contextual-left, .projects__sticky-nav__right');

      if (ctxHoverPages.includes(page) && overExcluded) {
        setCtxHover(true);
        if (mousePeek) { mousePeek = false; updatePeek(); }
      } else {
        setCtxHover(false);
        const next = !overExcluded;
        if (mousePeek !== next) { mousePeek = next; updatePeek(); }
      }
    });
  });

  // Reset peek when switching to list view — contextual row is the default there.
  // scrollPeek can survive a view switch if the user was scrolling up in grid view.
  nav.querySelector('.projects__sticky-nav__right')
    ?.addEventListener('click', () => {
      requestAnimationFrame(() => {
        if (window.Alpine?.store?.('projects')?.view === 'list') {
          scrollPeek = false;
          mousePeek  = false;
          updatePeek();
        }
      });
    });

  nav.addEventListener('mouseleave', () => {
    mouseleaveTimer = setTimeout(() => {
      mouseleaveTimer = null;
      mousePeek = false;
      updatePeek();
      setCtxHover(false);
    }, 50);
  });
}
